import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { invoicingApi } from 'src/utils/invoicingApi';
import { showToast } from '../../../../utils/Toast/SnackbarProvider';

const STATUS_LABELS = {
  planned: 'Planned',
  invoiced: 'Invoiced'
};

const EMPTY_FORM = { amount: '', invoicing_date: '', description: '' };

// currency-prefixed money formatter — mirrors Invoicing.js's formatMoney,
// but takes a raw value since it's used for both worklist row fields and
// individual schedule-row amounts.
const formatAmount = (row, value) => {
  if (value === null || value === undefined || value === '') return '—';
  const prefix = row?.currency?.label ? `${row.currency.label} ` : '';
  return `${prefix}${value}`;
};

// Flattens a DRF error response ({field: [msgs]} or {message: str}) into a
// single human-readable string for showToast.
const fieldErrorsToMessage = (err) => {
  const data = err?.response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data;
  if (data.message) return data.message;
  const parts = Object.values(data)
    .map((v) => (Array.isArray(v) ? v.join(' ') : v))
    .filter(Boolean);
  return parts.length ? parts.join(' ') : null;
};

const CompositionRow = ({ label, value, negative }) => (
  <TableRow>
    <TableCell sx={{ border: 0, color: 'text.secondary', pl: 0 }}>{label}</TableCell>
    <TableCell sx={{ border: 0, textAlign: 'right', pr: 0, fontWeight: 600, color: negative ? 'error.main' : undefined }}>
      {value}
    </TableCell>
  </TableRow>
);

export default function InvoiceScheduleDrawer({ open, row, onClose, onChanged }) {
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [savingEditId, setSavingEditId] = useState(null);
  const [actionRowId, setActionRowId] = useState(null); // row busy with mark/remove

  const fetchInvoices = useCallback(() => {
    if (!row?.id) return;
    setLoadingInvoices(true);
    invoicingApi
      .getInvoices(row.id)
      .then((res) => setInvoices(Array.isArray(res?.data) ? res.data : []))
      .catch((err) => {
        showToast(err?.response?.data?.message || 'Failed to load invoice schedule', 'error');
      })
      .finally(() => setLoadingInvoices(false));
  }, [row?.id]);

  useEffect(() => {
    if (open && row?.id) {
      setAddForm(EMPTY_FORM);
      setEditingId(null);
      fetchInvoices();
    } else if (!open) {
      setInvoices([]);
      setAddForm(EMPTY_FORM);
      setEditingId(null);
    }
  }, [open, row?.id, fetchInvoices]);

  // Every successful mutation: refresh this drawer's schedule table AND tell
  // Invoicing.js to refetch the worklist (so derived totals/composition on
  // the list recompute), then toast success.
  const afterMutation = (successMessage) => {
    fetchInvoices();
    if (onChanged) onChanged();
    showToast(successMessage, 'success');
  };

  const validateAmountAndDate = (amount, date) => {
    if (amount === '' || amount === null || amount === undefined) {
      showToast('Amount is required.', 'error');
      return false;
    }
    const numericAmount = Number(amount);
    if (Number.isNaN(numericAmount) || numericAmount < 0) {
      showToast('Amount must be non-negative.', 'error');
      return false;
    }
    if (!date) {
      showToast('Invoicing date is required.', 'error');
      return false;
    }
    return true;
  };

  const handleAddChange = (field) => (e) => setAddForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAddSubmit = () => {
    if (!validateAmountAndDate(addForm.amount, addForm.invoicing_date)) return;
    setAdding(true);
    invoicingApi
      .createInvoice({
        company: row.id,
        amount: addForm.amount,
        invoicing_date: addForm.invoicing_date,
        status: 'planned',
        description: addForm.description || undefined
      })
      .then(() => {
        setAddForm(EMPTY_FORM);
        afterMutation('Invoice added.');
      })
      .catch((err) => {
        showToast(fieldErrorsToMessage(err) || 'Failed to add invoice', 'error');
      })
      .finally(() => setAdding(false));
  };

  const startEdit = (invoice) => {
    setEditingId(invoice.id);
    setEditForm({
      amount: invoice.amount ?? '',
      invoicing_date: invoice.invoicing_date ?? '',
      description: invoice.description ?? ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
  };

  const handleEditChange = (field) => (e) => setEditForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleEditSubmit = (invoice) => {
    if (!validateAmountAndDate(editForm.amount, editForm.invoicing_date)) return;
    setSavingEditId(invoice.id);
    // NOTE: never send `company` on update — the backend rejects reassignment with a 400.
    invoicingApi
      .updateInvoice(invoice.id, {
        amount: editForm.amount,
        invoicing_date: editForm.invoicing_date,
        description: editForm.description
      })
      .then(() => {
        cancelEdit();
        afterMutation('Invoice updated.');
      })
      .catch((err) => {
        showToast(fieldErrorsToMessage(err) || 'Failed to update invoice', 'error');
      })
      .finally(() => setSavingEditId(null));
  };

  const handleMarkInvoiced = (invoice) => {
    setActionRowId(invoice.id);
    invoicingApi
      .markInvoiced(invoice.id)
      .then(() => afterMutation('Invoice marked as Invoiced.'))
      .catch((err) => {
        showToast(err?.response?.data?.message || 'Failed to mark invoice as invoiced', 'error');
      })
      .finally(() => setActionRowId(null));
  };

  const handleRemove = (invoice) => {
    setActionRowId(invoice.id);
    invoicingApi
      .removeInvoice(invoice.id)
      .then((res) => afterMutation(res?.data?.message || 'Invoice removed.'))
      .catch((err) => {
        showToast(err?.response?.data?.message || 'Failed to remove invoice', 'error');
      })
      .finally(() => setActionRowId(null));
  };

  const outstandingValue = row?.outstanding === null || row?.outstanding === undefined ? null : Number(row.outstanding);
  const outstandingIsNegative = outstandingValue !== null && outstandingValue < 0;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '100vw', sm: 680 }, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {row?.company_name || 'Invoice schedule'}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            {row?.bm ? `BM: ${row.bm.first_name} ${row.bm.last_name}` : ''}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            p: 3,
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
            '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '10px' }
          }}
        >
          {/* Read-only TCV composition (Global Constraint 9 / SC-005) — no inputs, no save button. */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            TCV composition
          </Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableBody>
              <CompositionRow label="TCV" value={formatAmount(row, row?.total_contract_value)} />
              <CompositionRow label="TCI" value={formatAmount(row, row?.tci)} />
              <CompositionRow label="OneTime (SaaS)" value={formatAmount(row, row?.one_time_saas)} />
              <CompositionRow label="Other" value={formatAmount(row, row?.tcv_other)} />
              <CompositionRow label="MRR" value={formatAmount(row, row?.mrr)} />
              <CompositionRow label="Invoiced to date" value={formatAmount(row, row?.invoiced_to_date)} />
              <CompositionRow
                label="Outstanding"
                value={formatAmount(row, row?.outstanding)}
                negative={outstandingIsNegative}
              />
            </TableBody>
          </Table>

          <Divider sx={{ mb: 3 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Invoice schedule
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} alignItems={{ sm: 'flex-start' }}>
            <TextField
              label="Amount"
              type="number"
              size="small"
              value={addForm.amount}
              onChange={handleAddChange('amount')}
              sx={{ width: { xs: '100%', sm: 130 } }}
            />
            <TextField
              label="Invoicing date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={addForm.invoicing_date}
              onChange={handleAddChange('invoicing_date')}
              sx={{ width: { xs: '100%', sm: 170 } }}
            />
            <TextField
              label="Description (optional)"
              size="small"
              value={addForm.description}
              onChange={handleAddChange('description')}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
            <Button variant="contained" onClick={handleAddSubmit} disabled={adding} sx={{ height: 40 }}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </Stack>

          {loadingInvoices ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : invoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No invoices scheduled yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Amount</TableCell>
                    <TableCell>Invoicing date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => {
                    const isEditing = editingId === invoice.id;
                    const busy = actionRowId === invoice.id || savingEditId === invoice.id;
                    const statusLabel = STATUS_LABELS[invoice.status] || invoice.status;

                    if (isEditing) {
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={editForm.amount}
                              onChange={handleEditChange('amount')}
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="date"
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              value={editForm.invoicing_date}
                              onChange={handleEditChange('invoicing_date')}
                              sx={{ width: 150 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip size="small" label={statusLabel} color={invoice.status === 'invoiced' ? 'success' : 'default'} />
                          </TableCell>
                          <TableCell>
                            <TextField size="small" value={editForm.description} onChange={handleEditChange('description')} />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" variant="contained" onClick={() => handleEditSubmit(invoice)} disabled={busy}>
                                Save
                              </Button>
                              <Button size="small" onClick={cancelEdit} disabled={busy}>
                                Cancel
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return (
                      <TableRow key={invoice.id}>
                        <TableCell>{formatAmount(row, invoice.amount)}</TableCell>
                        <TableCell>{invoice.invoicing_date || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={statusLabel} color={invoice.status === 'invoiced' ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell>{invoice.description || '—'}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                            <Button size="small" onClick={() => startEdit(invoice)} disabled={busy}>
                              Edit
                            </Button>
                            {invoice.status === 'planned' && (
                              <Button size="small" onClick={() => handleMarkInvoiced(invoice)} disabled={busy}>
                                Mark Invoiced
                              </Button>
                            )}
                            <Button size="small" color="error" onClick={() => handleRemove(invoice)} disabled={busy}>
                              Remove
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
